import { RefObject, useCallback, useEffect, useState } from 'react'

export interface ScrollEdges {
  canScrollUp: boolean
  canScrollDown: boolean
}

/**
 * Tracks whether a scrollable element has more content above/below
 * the current viewport.
 *
 * @param scrollRef   the element to read scroll position from (must have real scrollTop)
 * @param observeRef  the element to watch for size changes; defaults to scrollRef.
 *                     Pass a separate ref when the scroll container's own size is
 *                     stable but its content wrapper resizes independently.
 * @param deps        extra dependencies that should trigger a re-check (e.g. data changes)
 */
export function useScrollEdges(
  scrollRef: RefObject<HTMLElement | null>,
  observeRef: RefObject<HTMLElement | null> = scrollRef,
  deps: unknown[] = []
): ScrollEdges {
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)

  const check = useCallback(() => {
    const el = scrollRef.current

    if (!el) {
      return
    }

    const isAtTop = el.scrollTop < 1
    const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 1

    setCanScrollUp(!isAtTop)
    setCanScrollDown(!isAtBottom)
  }, [scrollRef])

  useEffect(() => {
    const scrollEl = scrollRef.current
    const observeEl = observeRef.current

    if (!scrollEl) {
      return
    }

    check()

    scrollEl.addEventListener('scroll', check, { passive: true })

    const resizeObserver = new ResizeObserver(check)

    resizeObserver.observe(scrollEl)
    if (observeEl && observeEl !== scrollEl) {
      resizeObserver.observe(observeEl)
    }

    return () => {
      scrollEl.removeEventListener('scroll', check)
      resizeObserver.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollRef, observeRef, check, ...deps])

  return { canScrollUp, canScrollDown }
}
